import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { Users, MapPin, Clock, Coins, TrendingUp } from "lucide-react";
import { convertCityToCountry } from "@/utils/cityToCountry";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCountryDisplayName } from "@/utils/countryTranslations";

interface Employee {
  id: string;
  invitation_status: string;
  accepted_at: string | null;
  residence_country?: string;
  current_country?: string;
  remote_work_requests?: Array<{
    id: string;
    status: string;
  }>;
  employee_points?: Array<{
    total_points: number;
    used_points: number;
  }>;
}
interface Company {
  id: string;
  name: string;
  employee_count: number;
}
interface EmployeeStatisticsProps {
  employees: Employee[];
  company: Company | null;
}
const COLORS = {
  primary: "hsl(var(--primary))",
  secondary: "hsl(var(--secondary-gray))",
  success: "hsl(var(--success))",
  warning: "hsl(var(--warning))",
  muted: "hsl(var(--muted-foreground))"
};
export const EmployeeStatistics = ({
  employees,
  company
}: EmployeeStatisticsProps) => {
  const { t, i18n } = useTranslation();
  const [countryData, setCountryData] = useState<Array<{name: string, value: number, percentage: number}>>([]);

  useEffect(() => {
    const processCountryData = async () => {
      // Calculate country distribution with city-to-country conversion
      const countryDistribution: Record<string, number> = {};
      
      for (const employee of employees) {
        const locationName = employee.current_country || employee.residence_country || t("dashboard.employees.unknownCountry", "Unknown");
        const countryName = await convertCityToCountry(locationName);
        const displayName = getCountryDisplayName(countryName, i18n.language);
        countryDistribution[displayName] = (countryDistribution[displayName] || 0) + 1;
      }
      
      const processedCountryData = Object.entries(countryDistribution).map(([country, count]) => ({
        name: country,
        value: count,
        percentage: Math.round(count / employees.length * 100)
      }));
      
      setCountryData(processedCountryData);
    };

    if (employees.length > 0) {
      processCountryData();
    }
  }, [employees, i18n.language, t]);

  // Calculate status distribution
  const statusDistribution = employees.reduce((acc, employee) => {
    let status = t("dashboard.employees.status.inactive", "Inactive");
    if (employee.accepted_at) {
      status = t("dashboard.employees.status.active", "Active");
    } else if (employee.invitation_status === 'invitation_envoyee') {
      status = t("dashboard.employees.status.invited", "Invited");
    }
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const statusData = Object.entries(statusDistribution).map(([status, count]) => ({
    name: status,
    value: count,
    percentage: Math.round(count / employees.length * 100)
  }));

  // Calculate remote work requests status
  const remoteWorkDistribution = employees.reduce((acc, employee) => {
    const requests = employee.remote_work_requests || [];
    requests.forEach(request => {
      const status = request.status === 'approved' 
        ? t("dashboard.reports.statusLabels.approved", "Approved") 
        : request.status === 'pending' 
        ? t("dashboard.reports.statusLabels.pending", "Pending") 
        : t("dashboard.reports.statusLabels.rejected", "Rejected");
      acc[status] = (acc[status] || 0) + 1;
    });
    return acc;
  }, {} as Record<string, number>);
  const remoteWorkData = Object.entries(remoteWorkDistribution).map(([status, count]) => ({
    name: status,
    value: count
  }));

  // Calculate total points
  const totalPoints = employees.reduce((sum, employee) => {
    const points = employee.employee_points?.[0];
    return sum + (points?.total_points || 0);
  }, 0);
  const availablePoints = employees.reduce((sum, employee) => {
    const points = employee.employee_points?.[0];
    return sum + ((points?.total_points || 0) - (points?.used_points || 0));
  }, 0);
  const colors = [COLORS.primary, COLORS.secondary, COLORS.success, COLORS.warning, COLORS.muted];
  const CustomTooltip = ({
    active,
    payload
  }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return <div className="bg-card border rounded-lg p-3 shadow-lg">
          <p className="font-medium">{data.name}</p>
          <p className="text-sm text-muted-foreground">
            {data.value} ({data.percentage || 0}%)
          </p>
        </div>;
    }
    return null;
  };
  if (employees.length === 0) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              {t("dashboard.employees.statistics.totalEmployees", "Total Employees")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>{t("dashboard.employees.noEmployees", "No employees")}</p>
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  return <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("dashboard.employees.statistics.totalEmployees", "Total Employees")}</p>
                <p className="text-3xl font-bold text-primary">{employees.length}</p>
              </div>
              <Users className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("dashboard.employees.statistics.activeEmployees", "Active Employees")}</p>
                <p className="text-3xl font-bold text-success">
                  {employees.filter(e => e.accepted_at).length}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-success" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("dashboard.employees.statistics.totalPoints", "Total Nest Points")}</p>
                <p className="text-3xl font-bold text-warning">{totalPoints}</p>
              </div>
              <Coins className="h-8 w-8 text-warning" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("dashboard.employees.statistics.availablePoints", "Available Points")}</p>
                <p className="text-3xl font-bold text-primary">{availablePoints}</p>
              </div>
              <Clock className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Country Distribution */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              {t("dashboard.employees.distribution.title", "Distribution by Current Country")}
            </CardTitle>
            <CardDescription>{t("dashboard.employees.distribution.subtitle", "Current employee locations")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={countryData} cx="50%" cy="50%" innerRadius={40} outerRadius={80} paddingAngle={2} dataKey="value">
                    {countryData.map((entry, index) => <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-2">
              {countryData.map((item, index) => <div key={item.name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: colors[index % colors.length]
                }} />
                    <span>{item.name}</span>
                  </div>
                  <span className="font-medium">{item.percentage}%</span>
                </div>)}
            </div>
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              {t("dashboard.employees.statusChart.title", "Employee Status")}
            </CardTitle>
            <CardDescription>
              {t("dashboard.employees.statusChart.subtitle", "Distribution by invitation status")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={40} outerRadius={80} paddingAngle={2} dataKey="value">
                    {statusData.map((entry, index) => <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-2">
              {statusData.map((item, index) => <div key={item.name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: colors[index % colors.length]
                }} />
                    <span>{item.name}</span>
                  </div>
                  <span className="font-medium">{item.percentage}%</span>
                </div>)}
            </div>
          </CardContent>
        </Card>

        {/* Remote Work Requests */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {t("dashboard.employees.remoteRequests.title", "Remote Work Requests")}
            </CardTitle>
            <CardDescription>
              {t("dashboard.employees.remoteRequests.subtitle", "Request status")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {remoteWorkData.length > 0 ? <>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={remoteWorkData}>
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 space-y-2">
                  {remoteWorkData.map((item, index) => <div key={item.name} className="flex items-center justify-between text-sm">
                      <span>{item.name}</span>
                      <span className="font-medium">{item.value}</span>
                    </div>)}
                </div>
              </> : <div className="text-center py-8 text-muted-foreground h-64 flex items-center justify-center">
                <div>
                  <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>{t("dashboard.employees.noRequests", "No requests")}</p>
                </div>
              </div>}
          </CardContent>
        </Card>
      </div>
    </div>;
};